import { logoutSession } from '@/shared/api/http/authSession'
import { ENV_CONFIG } from '@/shared/config/env'
import { getAuthToken } from '@/shared/lib/storage'
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr'
import { useEffect, useRef } from 'react'
import { apiNotificationHub } from '../service/notification.urls'
import type { RealtimeNotification } from '../service/types/notification.model'

interface Options {
  enabled: boolean
  onNotification: (notification: RealtimeNotification) => void
  onUnreadCount: (count: number) => void
  /** Gọi khi kết nối được dựng lại sau đứt: sự kiện lỡ không được phát lại nên phải refetch */
  onReconnect: () => void
  /** BE thu hồi một thông báo (payload = Id): danh sách và số chưa đọc của chuông phải tải lại */
  onRevoked?: (id: string) => void
}

/**
 * Kênh realtime thông báo qua SignalR (transport SSE, token qua query access_token).
 * Tự nối lại do thư viện đảm nhiệm (withAutomaticReconnect), FE không tự viết backoff.
 */
export const useNotificationStream = (options: Readonly<Options>) => {
  // Giữ handler mới nhất mà không buộc kết nối phải mở lại mỗi lần render
  const handlers = useRef(options)
  handlers.current = options
  const { enabled } = options

  useEffect(() => {
    if (!enabled) return

    const connection = new HubConnectionBuilder()
      .withUrl(`${ENV_CONFIG.VITE_BACKEND_BASE_URL}${apiNotificationHub}`, {
        transport: HttpTransportType.ServerSentEvents,
        // Gọi lại ở mỗi lần (nối lại) nên luôn lấy token mới nhất
        accessTokenFactory: () => getAuthToken() ?? '',
      })
      .withAutomaticReconnect()
      .build()

    connection.on('ReceiveNotification', (payload: RealtimeNotification) =>
      handlers.current.onNotification(payload)
    )
    connection.on('UnreadCountChanged', (count: number) =>
      handlers.current.onUnreadCount(Number(count))
    )
    connection.on('NotificationRevoked', (id: string) => handlers.current.onRevoked?.(String(id)))
    connection.on('SessionEnded', () => {
      // Phiên bị BE kết thúc: dừng hẳn (không tự nối lại) rồi xử lý như phiên hết hạn
      connection.stop().catch(() => {})
      logoutSession()
    })
    connection.onreconnected(() => handlers.current.onReconnect())

    connection.start().catch(() => {
      // Lỗi mở lần đầu (hoặc bị stop lúc cleanup, vd StrictMode) — không có gì để làm thêm;
      // refetch REST vẫn là nguồn dữ liệu chính
    })

    return () => {
      connection.off('ReceiveNotification')
      connection.off('UnreadCountChanged')
      connection.off('NotificationRevoked')
      connection.off('SessionEnded')
      connection.stop().catch(() => {})
    }
  }, [enabled])
}
